import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllAttendance,
  getAttendanceById,
  getSpecificStudentAttendanceById,
  getSpecificTeacherAttendanceById,
  getSpecificStaffAttendanceById,
  createAttendance,
  updateAttendance,
  deleteAttendance,
} from '../services/attendanceService';
import Notification from '../components/Common/Notification';

export const AttendanceContext = createContext();

export const AttendanceProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllAttendanceRecords = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllAttendance(authHeaders);
      setAttendanceRecords(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching attendance records.';
      showNotification(errorMessage, true);
    }
  };

  const fetchAttendanceById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAttendanceById(id, authHeaders);
      setSelectedAttendance(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching attendance record with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchSpecificStudentAttendanceById = async (studentId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getSpecificStudentAttendanceById(studentId, authHeaders);

      setSelectedAttendance(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching attendance records for student ID ${studentId}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchSpecificTeacherAttendanceById = async (teacherId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getSpecificTeacherAttendanceById(teacherId, authHeaders);
      setSelectedAttendance(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching attendance records for teacher ID ${teacherId}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchSpecificStaffAttendanceById = async (staffId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getSpecificStaffAttendanceById(staffId, authHeaders);
      setSelectedAttendance(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching attendance records for staff ID ${staffId}.`;
      showNotification(errorMessage, true);
    }
  };

  const addAttendanceRecord = async (attendanceData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newAttendance = await createAttendance(attendanceData, authHeaders);
      setAttendanceRecords(prevAttendanceRecords => [...prevAttendanceRecords, newAttendance]);
      showNotification('Attendance record added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding attendance record.';
      showNotification(errorMessage, true);
    }
  };

  const editAttendanceRecord = async (id, attendanceData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedAttendance = await updateAttendance(id, attendanceData, authHeaders);
      setAttendanceRecords(prevAttendanceRecords =>
        prevAttendanceRecords.map(ar => (ar.id === id ? updatedAttendance : ar))
      );
      showNotification('Attendance record updated successfully!', false);
      return updatedAttendance;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating attendance record.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeAttendanceRecord = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteAttendance(id, authHeaders);
      setAttendanceRecords(prevAttendanceRecords => prevAttendanceRecords.filter(ar => ar.id !== id));
      showNotification('Attendance record removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing attendance record.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllAttendanceRecords();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <AttendanceContext.Provider
      value={{
        attendanceRecords,
        selectedAttendance,
        fetchAllAttendanceRecords,
        fetchAttendanceById,
        fetchSpecificStudentAttendanceById,
        fetchSpecificTeacherAttendanceById,
        fetchSpecificStaffAttendanceById,
        addAttendanceRecord,
        editAttendanceRecord,
        removeAttendanceRecord,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </AttendanceContext.Provider>
  );
};

// Hook to use AttendanceContext
export const useAttendance = () => useContext(AttendanceContext);
